import Constants, { SystemInsightsConsts } from 'client/util/Constants';
import SystemInsightsApi from 'client/api/v2/SystemInsightsApi';
import Util from 'client/util/Util';
import moment from 'moment';

const {
  dateFormat,
  dateTimeLongFormat,
  linux,
  mac,
  dataNotAvailable,
  windows,
} = Constants;

const {
  network,
  operatingSystem,
  software,
  systemAndHardware,
  usersAndGroups,
} = SystemInsightsConsts.groups;

/**
 * A list of metric group keys for reference
 */
const metricGroups = {
  applicationLayerFirewall: 'applicationLayerFirewall',
  applications: 'applications',
  battery: 'battery',
  bitlockerInfo: 'bitlockerInfo',
  browserPlugins: 'browserPlugins',
  certificates: 'certificates',
  chromeExtensions: 'chromeExtensions',
  crashes: 'crashes',
  cupsDestinations: 'cupsDestinations',
  diskEncryption: 'diskEncryption',
  diskInfo: 'diskInfo',
  etcHosts: 'etcHosts',
  firefoxAddons: 'firefoxAddons',
  linux_packages: 'linux_packages',
  kernel: 'kernel',
  ieExtensions: 'ieExtensions',
  interfaceAddrs: 'interfaceAddrs',
  interfaceDetails: 'interfaceDetails',
  launchD: 'launchD',
  logicalDrives: 'logicalDrives',
  managedPolicies: 'managedPolicies',
  mounts: 'mounts',
  patches: 'patches',
  programs: 'programs',
  safariExtensions: 'safariExtensions',
  scheduledTasks: 'scheduledTasks',
  secureBoot: 'secureBoot',
  services: 'services',
  shadow: 'shadow',
  sharedFolders: 'sharedFolders',
  sharedResources: 'sharedResources',
  sharingPreferences: 'sharingPreferences',
  sipConfig: 'sipConfig',
  startupItems: 'startupItems',
  systemControls: 'systemControls',
  systemInfo: 'systemInfo',
  tpmInfo: 'tpmInfo',
  usbDevices: 'usbDevices',
  userSshKeys: 'userSshKeys',
  users: 'users',
};

/**
 * This is a collection of information of the metric groups that the admin may view.
 * Each metric group is presented as a dropdown option in the details section of System Insights.
 * @param apiMethod - The SystemInsightsApi method that should be called to retrieve the data
 * @param columns - An array of fields to show the user, if applicable
 * @param dataKey - The unique data key that the data for the metric group is stored in
 * @param label - The dropdown option label
 * @param table - The name of the underlying System Insights table
 * @param supportedOs - An array of os's that the API endpoint supports
 */
const metricGroupMetadata = {
  [metricGroups.applicationLayerFirewall]: {
    dataFormat: {
      allow_signed_enabled: d => (d ? 'Enabled' : 'Disabled'),
      collection_time: d => moment(d).format(dateTimeLongFormat),
      firewall_unload: d => (d ? 'Enabled' : 'Disabled'),
      global_state: d => (d ? 'Enabled' : 'Disabled'),
      logging_enabled: d => (d ? 'Enabled' : 'Disabled'),
      logging_option: d => (d ? 'Enabled' : 'Disabled'),
      stealth_enabled: d => (d ? 'Enabled' : 'Disabled'),
    },
    group: network,
    label: 'Application Layer Firewall',
    table: 'alf',
    supportedOs: [mac],
  },
  [metricGroups.applications]: {
    columns: [
      {
        getDataValue: d => d.display_name || d.bundle_name || d.name,
        name: 'Name',
      },
      { dataField: 'bundle_short_version', name: 'Version' },
      {
        getDataValue: d => (
          d.last_opened_time === -1
            ? dataNotAvailable
            : moment.unix(d.last_opened_time).format(dateFormat)
        ),
        name: 'Last Opened',
      },
    ],
    defaultSort: 'name',
    group: software,
    label: 'Applications',
    table: 'apps',
    supportedOs: [mac],
  },
  [metricGroups.battery]: {
    group: systemAndHardware,
    label: 'Battery',
    table: 'battery',
    supportedOs: [mac],
  },
  [metricGroups.bitlockerInfo]: {
    columns: [
      { dataField: 'drive_letter', name: 'Drive' },
      {
        getDataValue: d => {
          const percentageEncrypted = parseInt(d.percentage_encrypted, 10);
          const percentageDecrypted = 100 - percentageEncrypted;
          if (d.protection_status === 1) return 'Encrypted';
          if (d.conversion_status === 0) return 'Decrypted';
          if (d.conversion_status === 1) return 'Encrypted (Suspended)';
          if (d.conversion_status === 2) return `Encrypting...(${percentageEncrypted}%)`;
          if (d.conversion_status === 3) return `Decrypting...(${percentageDecrypted}%)`;
          if (d.conversion_status === 4) return `Encryption Paused (${percentageEncrypted}%)`;
          if (d.conversion_status === 5) return `Decryption Paused (${percentageDecrypted}%)`;
          return 'Unknown';
        },
        name: 'Encryption Status',
      },
      { dataField: 'encryption_method', name: 'Encryption Method' },
    ],
    defaultSort: 'drive_letter',
    group: systemAndHardware,
    label: 'Bitlocker Info',
    table: 'bitlocker_info',
    supportedOs: [windows],
  },
  [metricGroups.browserPlugins]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'version', name: 'Version' },
      {
        getDataValue: d => (d.disabled ? 'Disabled' : 'Enabled'),
        name: 'Status',
      },
    ],
    defaultSort: 'name',
    group: software,
    label: 'Browser Plugins',
    table: 'browser_plugins',
    supportedOs: [mac],
  },
  [metricGroups.certificates]: {
    columns: [
      { dataField: 'common_name', name: 'Name' },
      {
        getDataValue: d => moment.unix(d.not_valid_after).format(dateFormat),
        name: 'Expiration Date',
      },
    ],
    defaultSort: 'common_name',
    group: systemAndHardware,
    label: 'Certificates',
    table: 'certificates',
    supportedOs: [mac, windows],
  },
  [metricGroups.chromeExtensions]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'version', name: 'Version' },
    ],
    defaultSort: 'name',
    group: software,
    label: 'Chrome Extensions',
    table: 'chrome_extensions',
    supportedOs: [linux, mac, windows],
  },
  [metricGroups.crashes]: {
    columns: [
      { dataField: 'type', name: 'Type' },
      { dataField: 'responsible', name: 'Process' },
      {
        getDataValue: d => moment(new Date(d.datetime)).format(dateTimeLongFormat),
        name: 'Date/Time',
      },
    ],
    defaultSort: '-datetime',
    group: systemAndHardware,
    label: 'Crashes',
    table: 'crashes',
    supportedOs: [mac],
  },
  [metricGroups.cupsDestinations]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'option_name', name: 'Field' },
      { dataField: 'option_value', name: 'Value' },
    ],
    group: systemAndHardware,
    label: 'Printers',
    table: 'cups_destinations',
    supportedOs: [mac],
  },
  [metricGroups.diskEncryption]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      {
        getDataValue: d => (d.encrypted ? 'On' : 'Off'),
        name: 'Disk Encrypted',
      },
    ],
    group: systemAndHardware,
    label: 'Disk Encryption',
    table: 'disk_encryption',
    supportedOs: [mac, linux],
  },
  [metricGroups.diskInfo]: {
    dataFormat: {
      collection_time: d => moment(d).format(dateTimeLongFormat),
      disk_size: d => {
        if (d === '-1') return dataNotAvailable;
        const size = parseInt(d, 10);
        const { number, unit } = Util.getTextToDisplayBytes(size, true);
        return `${number} ${unit}`;
      },
    },
    group: systemAndHardware,
    label: 'Disk Info',
    table: 'disk_info',
    supportedOs: [windows],
  },
  [metricGroups.etcHosts]: {
    columns: [
      { dataField: 'hostnames', name: 'Hostname' },
      { dataField: 'address', name: 'Address' },
    ],
    defaultSort: 'address',
    group: network,
    label: 'ETC Hosts',
    table: 'etc_hosts',
    supportedOs: [linux, mac, windows],
  },
  [metricGroups.firefoxAddons]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'version', name: 'Version' },
      {
        getDataValue: d => (d.active ? 'Enabled' : 'Disabled'),
        name: 'Status',
      },
    ],
    defaultSort: 'name',
    group: software,
    label: 'Firefox Addons',
    table: 'firefox_addons',
    supportedOs: [linux, mac],
  },
  [metricGroups.linux_packages]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'version', name: 'Version' },
      { dataField: 'release_or_revision', name: 'Release or Revision' },
      { dataField: 'maintainer_or_vendor', name: 'Maintainer or Vendor' },
      {
        getDataValue: d => (
          d.install_time ? moment.unix(d.install_time).format(dateTimeLongFormat) : dataNotAvailable
        ),
        name: 'Install Date',
      },
    ],
    defaultSort: 'name',
    group: software,
    label: 'Packages',
    table: 'linux_packages',
    supportedOs: [linux],
  },
  [metricGroups.ieExtensions]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'registry_path', name: 'Registry Path' },
      { dataField: 'version', name: 'Version' },
    ],
    group: software,
    label: 'Internet Explorer Extensions',
    table: 'ie_extensions',
    supportedOs: [windows],
  },
  [metricGroups.interfaceAddrs]: {
    columns: [
      { dataField: 'interface', name: 'Interface' },
      { dataField: 'address', name: 'Address' },
    ],
    defaultSort: 'interface',
    group: network,
    label: 'Interface Addresses',
    table: 'interface_addresses',
    supportedOs: [linux, mac, windows],
  },
  [metricGroups.interfaceDetails]: {
    columns: [
      { dataField: 'interface', name: 'Interface' },
      { dataField: 'mac', name: 'MAC Address' },
      { dataField: 'connection_status', name: 'Connection Status' },
      { dataField: 'link_speed', name: 'Bandwidth (MB/s)' },
    ],
    defaultSort: 'interface',
    group: network,
    label: 'Interface Details',
    table: 'interface_details',
    supportedOs: [linux, mac, windows],
  },
  [metricGroups.kernel]: {
    dataFormat: {
      collection_time: d => moment(d).format(dateTimeLongFormat),
    },
    group: operatingSystem,
    label: 'Kernel',
    table: 'kernel_info',
    supportedOs: [linux, mac, windows],
  },
  [metricGroups.launchD]: {
    columns: [
      { dataField: 'label', name: 'Label' },
      {
        getDataValue: d => (d.run_at_load === '1' ? 'Enabled' : 'Disabled'),
        name: 'Run At Load',
      },
    ],
    defaultSort: 'label',
    group: software,
    label: 'Launchd',
    table: 'launchd',
    supportedOs: [mac],
  },
  [metricGroups.logicalDrives]: {
    columns: [
      { dataField: 'device_id', name: 'Device' },
      {
        getDataValue: (d) => {
          if (d.size === '-1') return dataNotAvailable;
          const size = parseInt(d.size, 10);
          const storageObj = Util.getTextToDisplayBytes(size, true);
          return `${storageObj.number} ${storageObj.unit}`;
        },
        name: 'Storage',
      },
      {
        getDataValue: (d) => {
          if (d.free_space === '-1') return dataNotAvailable;
          const freeSpace = parseInt(d.free_space, 10);
          const size = parseInt(d.size, 10);
          const percentage = size === 0 ? 0 : Math.ceil((100 * freeSpace) / size);
          const storageObj = Util.getTextToDisplayBytes(freeSpace, true);
          return `${storageObj.number} ${storageObj.unit} (${percentage}%)`;
        },
        name: 'Storage Available',
      },
    ],
    defaultSort: 'device_id',
    group: systemAndHardware,
    label: 'Logical Drives',
    table: 'logical_drives',
    supportedOs: [windows],
  },
  [metricGroups.managedPolicies]: {
    columns: [
      { dataField: 'domain', name: 'Domain' },
      { dataField: 'name', name: 'Field' },
      { dataField: 'value', name: 'Value' },
    ],
    group: systemAndHardware,
    label: 'Managed Policies',
    table: 'managed_policies',
    supportedOs: [mac],
  },
  [metricGroups.mounts]: {
    columns: [
      { dataField: 'device', name: 'Device' },
      {
        getDataValue: (d) => {
          if (d.blocks === '-1') return dataNotAvailable;
          const blocks = parseInt(d.blocks, 10) * parseInt(d.blocks_size, 10);
          const storageObj = Util.getTextToDisplayBytes(blocks, false);
          return `${storageObj.number} ${storageObj.unit}`;
        },
        name: 'Storage',
      },
      {
        getDataValue: (d) => {
          if (d.blocks_available === '-1') return dataNotAvailable;
          const blocksAvailable = parseInt(d.blocks_available, 10) * parseInt(d.blocks_size, 10);
          const blocks = parseInt(d.blocks, 10) * parseInt(d.blocks_size, 10);
          const percentage = blocks === 0 ? 0 : Math.ceil((100 * blocksAvailable) / blocks);
          const storageObj = Util.getTextToDisplayBytes(blocksAvailable, false);
          return `${storageObj.number} ${storageObj.unit} (${percentage}%)`;
        },
        name: 'Storage Available',
      },
    ],
    defaultSort: 'device',
    group: systemAndHardware,
    label: 'Mounts',
    table: 'mounts',
    supportedOs: [linux, mac],
  },
  [metricGroups.patches]: {
    columns: [
      { dataField: 'hotfix_id', name: 'Patch' },
      { dataField: 'description', name: 'Description' },
      { dataField: 'installed_on', name: 'Install Date' },
    ],
    defaultSort: '-installed_on',
    group: systemAndHardware,
    label: 'Patches',
    table: 'patches',
    supportedOs: [windows],
  },
  [metricGroups.programs]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'version', name: 'Version' },
      {
        getDataValue: d => (
          d.install_date ? moment(d.install_date).format(dateFormat) : dataNotAvailable
        ),
        name: 'Install Date',
      },
    ],
    group: software,
    label: 'Programs',
    table: 'programs',
    supportedOs: [windows],
  },
  [metricGroups.safariExtensions]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'version', name: 'Version' },
    ],
    defaultSort: 'name',
    group: software,
    label: 'Safari Extensions',
    table: 'safari_extensions',
    supportedOs: [mac],
  },
  [metricGroups.secureBoot]: {
    columns: [
      {
        getDataValue: d => ((d.secure_boot === 1 || d.secure_boot === '1') ? 'Enabled' : 'Disabled'),
        name: 'Secure Boot Enabled',
      },
      {
        getDataValue: d => ((d.setup_mode === 1 || d.setup_mode === '1') ? 'Enabled' : 'Disabled'),
        name: 'Setup Mode Enabled',
      },
    ],
    defaultSort: 'secure_boot',
    group: systemAndHardware,
    label: 'Secure Boot',
    table: 'secureboot',
    supportedOs: [linux, windows],
  },
  [metricGroups.scheduledTasks]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'state', name: 'Status' },
      {
        getDataValue: d => moment.unix(d.last_run_time).format(dateTimeLongFormat),
        name: 'Last Run Time',
      },
    ],
    defaultSort: '-last_run_time',
    group: systemAndHardware,
    label: 'Scheduled Tasks',
    table: 'scheduled_tasks',
    supportedOs: [windows],
  },
  [metricGroups.services]: {
    columns: [
      { dataField: 'display_name', name: 'Name' },
      { dataField: 'status', name: 'Status' },
      { dataField: 'start_type', name: 'Start Type' },
    ],
    defaultSort: 'display_name',
    group: systemAndHardware,
    label: 'Services',
    table: 'services',
    supportedOs: [windows],
  },
  [metricGroups.shadow]: {
    columns: [
      { dataField: 'username', name: 'User' },
      {
        getDataValue: d => moment.unix(d.last_change).format(dateFormat),
        name: 'Password Change Date',
      },
    ],
    defaultSort: 'last_change',
    group: usersAndGroups,
    label: 'Shadow',
    table: 'shadow',
    supportedOs: [linux],
  },
  [metricGroups.sharedFolders]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'path', name: 'Path' },
    ],
    defaultSort: 'name',
    group: systemAndHardware,
    label: 'Shared Folders',
    table: 'shared_folders',
    supportedOs: [mac],
  },
  [metricGroups.sharedResources]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'description', name: 'Description' },
      { dataField: 'status', name: 'Status' },
    ],
    defaultSort: 'name',
    group: systemAndHardware,
    label: 'Shared Resources',
    table: 'shared_resources',
    supportedOs: [windows],
  },
  [metricGroups.sharingPreferences]: {
    dataFormat: {
      bluetooth_sharing: d => (d ? 'Enabled' : 'Disabled'),
      collection_time: d => moment(d).format(dateTimeLongFormat),
      content_caching: d => (d ? 'Enabled' : 'Disabled'),
      disc_sharing: d => (d ? 'Enabled' : 'Disabled'),
      file_sharing: d => (d ? 'Enabled' : 'Disabled'),
      internet_sharing: d => (d ? 'Enabled' : 'Disabled'),
      printer_sharing: d => (d ? 'Enabled' : 'Disabled'),
      remote_apple_events: d => (d ? 'Enabled' : 'Disabled'),
      remote_login: d => (d ? 'Enabled' : 'Disabled'),
      remote_management: d => (d ? 'Enabled' : 'Disabled'),
      screen_sharing: d => (d ? 'Enabled' : 'Disabled'),
    },
    group: systemAndHardware,
    label: 'Sharing Preferences',
    table: 'sharing_preferences',
    supportedOs: [mac],
  },
  [metricGroups.sipConfig]: {
    columns: [
      { dataField: 'config_flag', name: 'Config Flag' },
      {
        getDataValue: d => (d.enabled === '1' ? 'Enabled' : 'Disabled'),
        name: 'Status',
      },
      {
        getDataValue: d => (d.enabled_nvram === '1' ? 'Enabled' : 'Disabled'),
        name: 'NVRAM Status',
      },
    ],
    defaultSort: 'config_flag',
    group: systemAndHardware,
    label: 'Apple\'s System Integrity Protection',
    table: 'sip_config',
    supportedOs: [mac],
  },
  [metricGroups.startupItems]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'type', name: 'Type' },
      { dataField: 'status', name: 'Status' },
    ],
    defaultSort: 'name',
    group: systemAndHardware,
    label: 'Startup Items',
    table: 'startup_items',
    supportedOs: [mac, windows],
  },
  [metricGroups.systemControls]: {
    columns: [
      { dataField: 'name', name: 'Name' },
      { dataField: 'subsystem', name: 'Subsystem' },
      { dataField: 'current_value', name: 'Current Value' },
    ],
    defaultSort: 'name',
    group: operatingSystem,
    label: 'System Controls',
    table: 'system_controls',
    supportedOs: [linux, mac],
  },
  [metricGroups.systemInfo]: {
    dataFormat: {
      collection_time: d => moment(d).format(dateTimeLongFormat),
      physical_memory: d => {
        if (d === '-1') return dataNotAvailable;
        const size = parseInt(d, 10);
        const { number, unit } = Util.getTextToDisplayBytes(size, false);
        return `${number} ${unit}`;
      },
    },
    group: systemAndHardware,
    label: 'System Info',
    table: 'system_info',
    supportedOs: [linux, mac, windows],
  },
  [metricGroups.tpmInfo]: {
    columns: [
      {
        getDataValue: d => ((d.activated === 1 || d.activated === '1') ? 'Activated' : 'Not Activated'),
        name: 'TPM Activated',
      },
      {
        getDataValue: d => ((d.enabled === 1 || d.enabled === '1') ? 'Enabled' : 'Disabled'),
        name: 'TPM Enabled',
      },
      {
        getDataValue: d => ((d.owned === 1 || d.owned === '1') ? 'Owned' : 'Not Owned'),
        name: 'TPM Owned',
      },
      { dataField: 'spec_version', name: 'TPM version' },
    ],
    defaultSort: 'activated',
    group: systemAndHardware,
    label: 'TPM Info',
    table: 'tpm_info',
    supportedOs: [windows],
  },
  [metricGroups.usbDevices]: {
    columns: [
      { dataField: 'model', name: 'Model' },
      {
        getDataValue: d => (d.removable ? 'True' : 'False'),
        name: 'Removable',
      },
    ],
    group: systemAndHardware,
    label: 'USB Devices',
    table: 'usb_devices',
    supportedOs: [linux, mac],
  },
  [metricGroups.userSshKeys]: {
    columns: [
      { dataField: 'uid', name: 'User ID' },
      { dataField: 'path', name: 'Path' },
      {
        getDataValue: d => (d.encrypted ? 'True' : 'False'),
        name: 'Encrypted',
      },
    ],
    defaultSort: 'encrypted',
    group: usersAndGroups,
    label: 'User SSH Key States',
    table: 'user_ssh_keys',
    supportedOs: [linux, mac],
  },
  [metricGroups.users]: {
    columns: [
      { dataField: 'username', name: 'Username' },
      {
        getDataValue: (d) => {
          if (d.ad_managed) {
            return 'Active Directory';
          }
          if (d.managed) {
            return 'JumpCloud';
          }
          return 'Unknown';
        },
        name: 'Managed by',
      },
      {
        getDataValue: d => (d.admin ? 'Admin' : 'Standard'),
        name: 'Permissions',
      },
      {
        getDataValue: d => (d.last_login ? moment.unix(d.last_login).format(dateTimeLongFormat) : 'No login data found'),
        name: 'Last Login',
      },
    ],
    defaultSort: 'username',
    group: usersAndGroups,
    label: 'Local User Accounts',
    table: 'users',
    supportedOs: [], // this value avoids showing this table in the system insights details dropdown
  },
};

Object.keys(metricGroupMetadata).forEach((key) => {
  const metadata = metricGroupMetadata[key];
  const { defaultSort, table } = metadata;

  metadata.apiMethod = (id, options = {}) => SystemInsightsApi.getSystemInsight(id, table, {
    ...options,
    params: {
      ...options.params,
      ...(defaultSort && { sort: defaultSort }),
    },
  });
});

const isDataAvailable = (value) => {
  if (value === '') return Constants.dataNotAvailable;
  return value;
};

// This function modifies getDataValue to return "-" if the field returns an empty string
const formatInsightsDataValue = () => {
  Object.keys(metricGroupMetadata).forEach((key) => {
    if (metricGroupMetadata[key].columns) {
      metricGroupMetadata[key].columns = metricGroupMetadata[key].columns.map((column) => {
        const newColumn = { ...column };

        if (typeof newColumn.getDataValue === 'function') {
          newColumn.getDataValue = d => isDataAvailable(column.getDataValue(d));
        } else {
          newColumn.getDataValue = d => isDataAvailable(d[column.dataField]);
        }
        return newColumn;
      });
    }
  });
  return metricGroupMetadata;
};

export default {
  getMetricGroupsByOs(os) {
    // For a given OS, return the metric groups that are supported in alphabetical order
    return Object.keys(metricGroups)
      .map(metricGroupKey => metricGroups[metricGroupKey])
      .filter(metricGroup => metricGroupMetadata[metricGroup].supportedOs.includes(os));
  },

  metricGroups,
  metricGroupMetadata: formatInsightsDataValue(),
};
